<template>
  <dp-container v-if="loading">
    <dp-loading />
  </dp-container>
  <dp-container v-else>
    <!-- Back Link -->
    <b-row v-if="isAdmin" class="mt-3 mb-4">
      <b-col cols="6">
        <b-link :to="{ name: 'AdminRedirect' }"> {{ '&#8592; Back' }} </b-link>
      </b-col>
    </b-row>
    <!-- Product Type Navbar -->
    <ProductTypeNavbar v-if="!isAdmin" />
    <!-- Pet Header & Marketing Banner -->
    <section>
      <marketing-banner
        variant="essentials"
        :city-name="city.name"
        :city-logo="city.letterLogo && city.letterLogo.original ? city.letterLogo.original : null"
        :pet="requiredPet ? requiredPet : null"
        :is-admin="isAdmin"
      />
    </section>
    <!-- STORE BODY -->
    <section>
      <b-row class="align-items-start" no-gutters>
        <!-- Collections List -->
        <b-col class="d-none d-lg-block collections-list sticky" lg="3" style="top: 1rem">
          <b-button
            v-for="group in filteredGroupsWithoutSearch"
            :key="group.id"
            variant="outline"
            class="btn-block text-left p-0"
            @click="scrollToHash(encodeURIComponent(group.name.replace(/\s+/g, '-').toLowerCase()))"
          >
            <p class="font-weight-bold h3">{{ group.name }}</p>
          </b-button>
          <b-link v-if="!showAllProducts" :to="{ name: 'EssentialsStoreAll' }">
            <p class="font-weight-bold text-primary cursor-pointer pt-2 h3">View All</p>
          </b-link>
        </b-col>
        <!-- Product Groups & Product Group Styles -->
        <b-col cols="12" lg="9">
          <section :class="{ 'pb-4': !isAdmin && !city.marketingAdminCopy && (marketingInfo.info || marketingInfo.img) }">
            <!-- Marketing -->
            <div v-if="marketingInfo.info" class="marketing-info d-flex align-items-center justify-content-start mb-4">
              <b-col v-if="marketingInfo.img" cols="auto" class="mx-0 px-0">
                <b-img :src="getImage(marketingInfo.img)" :alt="`${marketingInfo.info}`" width="80" height="80" fluid />
              </b-col>
              <!-- eslint-disable-next-line vue/no-v-html (Make sure it's always hard-coded) -->
              <b-col v-html="marketingInfo.info" />
            </div>
          </section>
          <!-- Search Products -->
          <search
            :key="`s_${searchKey}`"
            v-model="search"
            :groups="filteredGroups"
            :pet-id="petId"
            :show-collections-dropdown="false"
            class="search-bar w-100 mx-1 mx-lg-0 mb-md-4"
          />
          <!-- Essentials Collections -->
          <product-group
            id="collections"
            :groups="filteredGroups"
            :pet-id="petId"
            :show-all-tags="showAllProducts"
            class="mb-md-4"
            pet-essentials-type
          />
        </b-col>
      </b-row>
    </section>
    <section v-if="city.petOwnerStoreMessage && city.displayPetOwnerStoreMessageForCollars" class="text-center">
      <!-- eslint-disable-next-line vue/no-v-html -->
      <b-row v-html="city.petOwnerStoreMessage" />
    </section>
    <section v-show="filteredGroups.length === 0" id="search-empty" class="mt-5">
      <b-card overlay :img-src="require('@/assets/images/404.jpg')" img-fluid img-alt="" class="border-0">
        <b-card-title class="d-none d-lg-block display-4 font-weight-bold mt-4">Ooops... product not found!</b-card-title>
        <b-card-title class="d-block d-lg-none h2 font-weight-bold mt-3">Ooops... product not found!</b-card-title>
        <b-card-body v-if="productGroups.length > 0 && productGroups.length > 0" class="mt-lg-5">
          <b-button variant="primary" class="px-5" @click="clearSearch">View All Products</b-button>
        </b-card-body>
      </b-card>
    </section>
  </dp-container>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import getImage from '@/mixins/getImage';
import { DpContainer, DpLoading } from '@docupet/component-library';
import ProductGroup from '@/components/ProductGroup';
import Search from '@/components/Search';
import MarketingBanner from '../components/MarketingBanner';
import ProductTypeNavbar from '../components/ProductTypeNavbar';
import { NotificationMixins } from '@/mixins/notification';

export default {
  name: 'Essentials',
  components: {
    ProductTypeNavbar,
    MarketingBanner,
    DpContainer,
    DpLoading,
    ProductGroup,
    Search,
  },
  mixins: [NotificationMixins, getImage],
  props: {
    petId: {
      type: [String, Number],
      default: '',
      required: false,
    },
    showAllProducts: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  metaTag() {
    return this.$router.currentRoute.meta;
  },
  data() {
    return {
      search: '',
      searchKey: 0,
    };
  },
  computed: {
    ...mapState({
      cityId: (state) => state.cityId,
      orderId: (state) => state.orderId,
      city: (state) => state.user.city,
      order: (state) => state.order.order,
      loading: (state) => state.essentialProduct.loading || state.order.itemLoading,
      DYOTStyle: (state) => state.essentialProduct.DYOTStyle,
      productGroups: (state) => state.essentialProduct.groups,
      isAdmin: (state) => state.user.user.id && state.user.user.__typename == 'Administrator',
      isInternalAdmin: (state) =>
        state.user.user.id && state.user.user.__typename === 'Administrator' && state.user.user.roles?.includes('ROLE_INTERNAL'),
    }),
    ...mapGetters({
      somePetsWithLicense: 'order/somePetsWithLicense',
      getRequiredPetByPetId: 'order/getRequiredPetByPetId',
    }),
    route() {
      if (this.$route.path.includes('essentials')) {
        return 'essentials';
      } else {
        return 'default';
      }
    },
    requiredPet() {
      return this.getRequiredPetByPetId(this.petId, !(this.isAdmin && this.$router?.currentRoute?.query?.single));
    },
    filteredGroups() {
      try {
        let groups = [];
        this.productGroups.forEach((group) => {
          const matchingProducts = group.productStyles.filter((s) => s.name.toLowerCase().includes(this.search.toLowerCase()));
          if (matchingProducts.length > 0) groups.push({ ...group, productStyles: matchingProducts });
          else if (group.name.toLowerCase().includes(this.search.toLowerCase())) groups.push(group);
        });
        return groups;
      } catch (e) {
        return [];
      }
    },
    filteredGroupsWithoutSearch() {
      try {
        let groups = this.productGroups;
        return groups;
      } catch (e) {
        return [];
      }
    },
    marketingInfo() {
      return !this.isAdmin
        ? { info: this.city?.marketingCopy, img: this.city?.marketingCopyImage?.original }
        : { info: this.city?.marketingAdminCopy, img: this.city?.marketingAdminCopyImage?.original };
    },
  },
  watch: {
    search() {
      if (this.search) {
        this.$refs?.['group-search']?.scrollIntoView({ behavior: 'smooth' });
      }
    },
  },
  created() {
    (async () => {
      if (!this.loading) {
        if (!this.productGroups || this.productGroups?.length === 0) {
          await this.$store.dispatch('essentialProduct/fetchGroups', { cityId: this.city?.id || this.cityId });
        }
        if (this.petId && !this.order?.id) {
          await this.$store.dispatch('order/fetchOrder', { orderId: this.orderId });
        }
      }
      while (this.loading) await new Promise((resolve) => setTimeout(resolve, 1000));
    })().then(() => {
      if (this.petId && !this.order.groups?.map(({ pet }) => pet?.id)?.includes(this.petId)) {
        this.$router.push({ name: 'TagStore' });
      }
    });
  },
  methods: {
    clearSearch() {
      this.$nextTick(() => {
        this.search = '';
        this.searchKey++;
      });
    },
    scrollToHash(hash) {
      document.getElementById(hash)?.scrollIntoView({
        behavior: 'smooth',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@docupet/component-library/src/assets/styles/_variables';
h1 {
  margin-bottom: -2.5rem;
}
#search-empty {
  text-align: center;
  svg {
    color: $dp-gray-2;
    width: 35%;
    height: 35%;
  }
  h2 {
    margin-top: 2rem;
    color: $dp-gray-3;
  }
}
.cursor-pointer {
  cursor: pointer;
}
@media (max-width: 991px) {
  // Mobile
  .m-only {
    display: block !important;
  }
  // Desktop
  .d-only {
    display: none !important;
  }
}
@media (min-width: 991px) {
  // Mobile
  .d-only {
    display: block !important;
  }
  // Desktop
  .m-only {
    display: none !important;
  }
  .donation-info {
    margin-left: -15px;
  }
  .marketing-info {
    margin-left: -15px;
  }
}
.collections-list {
  padding-right: 3rem;
  a:hover h3 {
    color: $dp-navy;
  }
}
</style>
